import { useState, useEffect } from 'react'
import { db } from '../../lib/supabase'

export default function AdminSiteConfig() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState({
    org_name: '',
    site_url: '',
    logo_url: '',
    same_as: [],
    contact_phone: '',
    contact_email: '',
    address_street: '',
    address_locality: '',
    address_region: '',
    address_country: 'PE',
    postal_code: '',
    google_maps_url: '',
    latitude: null,
    longitude: null,
    opening_hours_json: []
  })

  // Temporary state for social links input
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    tiktok: '',
    linkedin: '',
    youtube: ''
  })

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      setLoading(true)
      const { data, error } = await db.getSiteConfig()
      if (error && error.code !== 'PGRST116') throw error
      
      if (data) {
        setConfig(data)
        
        // Parse social links - handle both string and array formats
        let sameAs = data.same_as || []
        if (typeof sameAs === 'string') {
          try {
            sameAs = JSON.parse(sameAs)
          } catch (e) {
            console.error('Error parsing same_as:', e)
            sameAs = []
          }
        }
        if (!Array.isArray(sameAs)) {
          sameAs = []
        }
        
        setSocialLinks({
          facebook: sameAs.find(url => url.includes('facebook')) || '',
          instagram: sameAs.find(url => url.includes('instagram')) || '',
          tiktok: sameAs.find(url => url.includes('tiktok')) || '',
          linkedin: sameAs.find(url => url.includes('linkedin')) || '',
          youtube: sameAs.find(url => url.includes('youtube')) || ''
        })
      }
    } catch (error) {
      console.error('Error loading config:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // Build same_as array from social links
      const sameAsArray = Object.values(socialLinks).filter(url => url.trim() !== '')
      
      const payload = {
        ...config,
        same_as: sameAsArray,
        latitude: config.latitude ? parseFloat(config.latitude) : null,
        longitude: config.longitude ? parseFloat(config.longitude) : null
      }

      const { error } = await db.updateSiteConfig(payload)
      if (error) throw error
      
      alert('✅ Configuración guardada exitosamente')
      loadConfig()
    } catch (error) {
      console.error('Error saving config:', error)
      alert('❌ Error al guardar la configuración')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem' }}>Cargando...</div>
  }

  return (
    <div style={{ padding: window.innerWidth < 768 ? '1rem' : '2rem', maxWidth: '100%', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          🏢 CONFIGURACIÓN DEL SITIO
        </h1>
        <p style={{ color: '#666' }}>
          Gestiona la información de la organización para SEO y JSON-LD
        </p>
      </div>

      <div style={{ 
        background: '#fff', 
        borderRadius: '0.5rem', 
        border: '1px solid #e5e5e5',
        padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Organization Info */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            📋 INFORMACIÓN DE LA ORGANIZACIÓN
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Nombre Oficial
              </label>
              <input
                type="text"
                value={config.org_name || ''}
                onChange={(e) => setConfig({ ...config, org_name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                URL del Sitio
              </label>
              <input
                type="url"
                value={config.site_url || ''}
                onChange={(e) => setConfig({ ...config, site_url: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                URL del Logo
              </label>
              <input
                type="url"
                value={config.logo_url || ''}
                onChange={(e) => setConfig({ ...config, logo_url: e.target.value })}
                placeholder="https://..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
              {config.logo_url && (
                <img 
                  src={config.logo_url} 
                  alt="Logo preview" 
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '80px', 
                    marginTop: '0.5rem',
                    objectFit: 'contain'
                  }} 
                />
              )}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div style={{ marginBottom: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e5e5' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            📞 INFORMACIÓN DE CONTACTO
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Teléfono
              </label>
              <input
                type="tel"
                value={config.contact_phone || ''}
                onChange={(e) => setConfig({ ...config, contact_phone: e.target.value })}
                placeholder="+51 999 999 999"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Email
              </label>
              <input
                type="email"
                value={config.contact_email || ''}
                onChange={(e) => setConfig({ ...config, contact_email: e.target.value })}
                placeholder="contacto@ejemplo.com"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div style={{ marginBottom: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e5e5' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            📍 DIRECCIÓN
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Calle
              </label>
              <input
                type="text"
                value={config.address_street || ''}
                onChange={(e) => setConfig({ ...config, address_street: e.target.value })}
                placeholder="Av. Carlos Izaguirre 271-275"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Distrito
              </label>
              <input
                type="text"
                value={config.address_locality || ''}
                onChange={(e) => setConfig({ ...config, address_locality: e.target.value })}
                placeholder="Independencia"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Provincia/Región
              </label>
              <input
                type="text"
                value={config.address_region || ''}
                onChange={(e) => setConfig({ ...config, address_region: e.target.value })}
                placeholder="Lima"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Código Postal
              </label>
              <input
                type="text"
                value={config.postal_code || ''}
                onChange={(e) => setConfig({ ...config, postal_code: e.target.value })}
                placeholder="15113"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                País
              </label>
              <input
                type="text"
                value={config.address_country || ''}
                onChange={(e) => setConfig({ ...config, address_country: e.target.value })}
                placeholder="PE"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
          </div>
        </div>

        {/* Geo Coordinates */}
        <div style={{ marginBottom: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e5e5' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            🗺️ COORDENADAS GEOGRÁFICAS
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Latitud
              </label>
              <input
                type="number"
                step="0.000001"
                value={config.latitude || ''}
                onChange={(e) => setConfig({ ...config, latitude: e.target.value })}
                placeholder="-11.990286"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Longitud
              </label>
              <input
                type="number"
                step="0.000001"
                value={config.longitude || ''}
                onChange={(e) => setConfig({ ...config, longitude: e.target.value })}
                placeholder="-77.062296"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                URL de Google Maps
              </label>
              <input
                type="url"
                value={config.google_maps_url || ''}
                onChange={(e) => setConfig({ ...config, google_maps_url: e.target.value })}
                placeholder="https://www.google.com/maps/..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div style={{ marginBottom: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e5e5' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            🌐 REDES SOCIALES
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Facebook
              </label>
              <input
                type="url"
                value={socialLinks.facebook}
                onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                placeholder="https://www.facebook.com/..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Instagram
              </label>
              <input
                type="url"
                value={socialLinks.instagram}
                onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                placeholder="https://www.instagram.com/..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                TikTok
              </label>
              <input
                type="url"
                value={socialLinks.tiktok}
                onChange={(e) => setSocialLinks({ ...socialLinks, tiktok: e.target.value })}
                placeholder="https://www.tiktok.com/..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                LinkedIn
              </label>
              <input
                type="url"
                value={socialLinks.linkedin}
                onChange={(e) => setSocialLinks({ ...socialLinks, linkedin: e.target.value })}
                placeholder="https://www.linkedin.com/..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                YouTube
              </label>
              <input
                type="url"
                value={socialLinks.youtube}
                onChange={(e) => setSocialLinks({ ...socialLinks, youtube: e.target.value })}
                placeholder="https://www.youtube.com/..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ 
          paddingTop: '2rem', 
          borderTop: '1px solid #e5e5e5',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: '#000',
              color: '#fff',
              padding: '0.75rem 2rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Guardando...' : '💾 Guardar Configuración'}
          </button>
        </div>
      </div>
    </div>
  )
}

